import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTaskStore } from '../../store/useTaskStore.ts';
import { useToastStore } from '../../store/useToastStore.ts';
import { Card } from '../../components/Shared/Card.tsx';
import { Loading } from '../../components/Shared/Loading.tsx';
import { Badge } from '../../components/Shared/Badge.tsx';
import { Button } from '../../components/Shared/Button.tsx';
import { Modal } from '../../components/Shared/Modal.tsx';
import { SlideOver } from '../../components/Shared/SlideOver.tsx';
import { EmptyState } from '../../components/Shared/EmptyState.tsx';
import { TaskForm } from './components/TaskForm.tsx';
import { ScheduleRulesManager } from './components/ScheduleRulesManager.tsx';
import { ChecklistEditor } from './components/ChecklistEditor.tsx';
import { useTranslation } from '../../i18n';
import { showApiError } from '../../errors/showApiError';
import { formatDateTime } from '../../utils/dateUtils';
import {
  ArrowLeft,
  Edit2,
  Trash2,
  Clock,
  Package,
  Building2,
  Briefcase,
  FileText,
  ListChecks,
} from 'lucide-react';

export function TaskDetail() {
  const { t } = useTranslation(['tasks', 'common']);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentTask, loading, error, fetchTaskById, deleteTask, clearCurrentTask } = useTaskStore();
  const { addToast } = useToastStore();

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [initialLoaded, setInitialLoaded] = useState(false);

  useEffect(() => {
    if (id) {
      setInitialLoaded(false);
      fetchTaskById(id).then(() => setInitialLoaded(true));
    }
    return () => clearCurrentTask();
  }, [id, fetchTaskById, clearCurrentTask]);

  const handleEditClose = () => {
    setIsEditModalOpen(false);
    if (id) {
      fetchTaskById(id);
    }
  };

  const goBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate('/tasks');
  };

  const handleDelete = async () => {
    if (!id) return;
    
    try {
      const deactivated = await deleteTask(id);
      addToast(t(deactivated ? 'tasks:messages.deactivated' : 'tasks:messages.deleted'), 'success');
      goBack();
    } catch (err) {
      showApiError(err);
    }
  };

  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return t('tasks:duration.minutes', { count: mins });
    if (mins === 0) return t('tasks:duration.hours', { count: hours });
    return t('tasks:duration.hoursMinutes', { hours, minutes: mins });
  };

  if (!initialLoaded && loading) {
    return <Loading />;
  }

  if (error || !currentTask) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <Button
          variant="secondary"
          size="sm"
          onClick={goBack}
          leftIcon={<ArrowLeft className="w-4 h-4" strokeWidth={2} />}
        >
          {t('common:actions.back')}
        </Button>
        <Card>
          <EmptyState
            variant="error"
            title={t('tasks:detail.notFound')}
            description={error || t('tasks:detail.notFoundDescription')}
            action={
              <Button variant="secondary" onClick={goBack}>
                {t('tasks:detail.backToList')}
              </Button>
            }
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <Button
        variant="secondary"
        size="sm"
        onClick={goBack}
        leftIcon={<ArrowLeft className="w-4 h-4" strokeWidth={2} />}
      >
        {t('common:actions.back')}
      </Button>

      {/* Header card */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-[22px] sm:text-[24px] font-semibold tracking-tight text-slate-900 truncate">
                {currentTask.name}
              </h1>
              <Badge variant={currentTask.isActive ? 'success' : 'default'}>
                {currentTask.isActive ? t('common:status.active') : t('common:status.inactive')}
              </Badge>
            </div>
            <p className="mt-0.5 text-[13px] text-slate-500">
              {t('tasks:detail.subtitle')}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0">
            <Button
              variant="secondary"
              onClick={() => setIsEditModalOpen(true)}
              leftIcon={<Edit2 className="w-4 h-4" strokeWidth={2} />}
              className="w-full sm:w-auto"
            >
              {t('common:actions.edit')}
            </Button>
            <Button
              variant="danger"
              onClick={() => setIsDeleteModalOpen(true)}
              leftIcon={<Trash2 className="w-4 h-4" strokeWidth={2} />}
              className="w-full sm:w-auto"
            >
              {t('common:actions.delete')}
            </Button>
          </div>
        </div>
      </div>

      {/* Main Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        {/* Service & Object Info */}
        <Card title={t('tasks:detail.assignment')}>
          <div className="space-y-4">
            <div>
              <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500 flex items-center gap-1.5 mb-1">
                <Briefcase className="w-3.5 h-3.5" strokeWidth={2} />
                {t('tasks:fields.service')}
              </div>
              <p className="text-[13px] font-medium text-slate-900">
                {currentTask.serviceName}
              </p>
            </div>
            <div>
              <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500 flex items-center gap-1.5 mb-1">
                <Building2 className="w-3.5 h-3.5" strokeWidth={2} />
                {t('tasks:fields.object')}
              </div>
              <p className="text-[13px] font-medium text-slate-900">
                {currentTask.objectName}
              </p>
            </div>
            {currentTask.customerName && (
              <div>
                <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500 flex items-center gap-1.5 mb-1">
                  <Building2 className="w-3.5 h-3.5" strokeWidth={2} />
                  {t('tasks:fields.customer')}
                </div>
                <p className="text-[13px] font-medium text-slate-900">
                  {currentTask.customerName}
                </p>
              </div>
            )}
          </div>
        </Card>

        {/* Duration */}
        <Card title={t('tasks:detail.scheduling')}>
          <div>
            <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500 flex items-center gap-1.5 mb-1">
              <Clock className="w-3.5 h-3.5" strokeWidth={2} />
              {t('tasks:detail.estimatedDuration')}
            </div>
            <p className="text-[28px] font-mono-num font-semibold text-[#00A871] tabular-nums leading-tight">
              {formatDuration(currentTask.estimatedDurationMinutes)}
            </p>
            <p className="text-[12px] text-slate-500 mt-1 tabular-nums">
              ({currentTask.estimatedDurationMinutes} {t('tasks:duration.short.minutes')})
            </p>
          </div>
        </Card>
      </div>

      {/* Schedule Rules */}
      <ScheduleRulesManager taskId={currentTask.taskId} />

      {/* Checklist — the steps the employee ticks off on site */}
      <Card
        title={
          <span className="flex items-center gap-2">
            <ListChecks className="w-4 h-4 text-slate-400" strokeWidth={2} />
            {t('tasks:checklist.title')}
          </span>
        }
      >
        <p className="text-[12px] text-slate-500 mb-3">{t('tasks:checklist.description')}</p>
        <ChecklistEditor taskId={currentTask.taskId} />
      </Card>

      {/* Description */}
      {currentTask.description && (
        <Card title={t('tasks:fields.description')}>
          <p className="text-[13px] text-slate-700 whitespace-pre-wrap">
            {currentTask.description}
          </p>
        </Card>
      )}

      {/* Materials */}
      {currentTask.materials && currentTask.materials.length > 0 && (
        <Card
          title={
            <span className="flex items-center gap-2">
              <Package className="w-4 h-4 text-slate-400" strokeWidth={2} />
              {t('tasks:detail.materials')}
            </span>
          }
        >
          <div className="space-y-1.5">
            {currentTask.materials.map((material, index) => (
              <div
                key={index}
                className="flex items-center justify-between py-2 px-3 rounded-md border border-slate-200 bg-slate-50"
              >
                <span className="text-[13px] font-medium text-slate-900">
                  {material.name}
                </span>
                {material.quantity && (
                  <span className="text-[12px] text-slate-500 tabular-nums">
                    {material.quantity}
                  </span>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Instructions */}
      {currentTask.instructions && (
        <Card
          title={
            <span className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-slate-400" strokeWidth={2} />
              {t('tasks:detail.instructions')}
            </span>
          }
        >
          <pre className="text-[13px] text-slate-700 whitespace-pre-wrap font-mono bg-slate-50 border border-slate-200 p-4 rounded-md">
{currentTask.instructions}
          </pre>
        </Card>
      )}

      {/* Metadata */}
      <Card title={t('tasks:detail.metadata')}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <div>
            <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
              {t('tasks:detail.createdAt')}
            </div>
            <p className="text-[13px] font-medium text-slate-900 mt-0.5 tabular-nums">
              {formatDateTime(new Date(currentTask.createdAt))}
            </p>
          </div>
          {currentTask.updatedAt && (
            <div>
              <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
                {t('tasks:detail.updatedAt')}
              </div>
              <p className="text-[13px] font-medium text-slate-900 mt-0.5 tabular-nums">
                {formatDateTime(new Date(currentTask.updatedAt))}
              </p>
            </div>
          )}
        </div>
      </Card>

      {/* Edit SlideOver */}
      <SlideOver
        isOpen={isEditModalOpen}
        onClose={handleEditClose}
        title={t('tasks:editTask')}
      >
        {isEditModalOpen && (
          <TaskForm
            task={{
              taskId: currentTask.taskId,
              serviceId: currentTask.serviceId,
              serviceName: currentTask.serviceName,
              objectId: currentTask.objectId,
              objectName: currentTask.objectName,
              name: currentTask.name,
              estimatedDurationMinutes: currentTask.estimatedDurationMinutes,
              isActive: currentTask.isActive,
            }}
            onClose={handleEditClose}
            objects={[]}
            services={[]}
          />
        )}
      </SlideOver>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title={t('tasks:deleteTask')}
      >
        <div className="space-y-3">
          <p className="text-[13px] text-slate-700">
            {t('tasks:confirmDelete', { name: currentTask.name })}
          </p>
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 pt-2">
            <Button variant="secondary" onClick={() => setIsDeleteModalOpen(false)} className="w-full sm:w-auto">
              {t('common:actions.cancel')}
            </Button>
            <Button variant="danger" onClick={handleDelete} className="w-full sm:w-auto">
              {t('common:actions.delete')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
